import { api } from "@/lib/apiClient";

export type EmployeeLedgerLine = {
  id: number;
  date: string | null;
  voucher: string | null;
  voucher_type: string | null;
  account_id: number | null;
  account_code: string | null;
  account_name: string | null;
  particulars: string[];
  from_to: string | null;
  debit: number;
  credit: number;
  balance: number;
  balance_side: "Dr" | "Cr";
};

export type EmployeeLedger = {
  employee: {
    id: number;
    code: string | null;
    name: string;
  } | null;
  filters: {
    employee_id: number | null;
    date_from: string | null;
    date_to: string | null;
  };
  lines: EmployeeLedgerLine[];
  totals: {
    opening_balance: number;
    opening_balance_side: "Dr" | "Cr";
    debit: number;
    credit: number;
    balance: number;
    balance_side: "Dr" | "Cr";
  };
};

type ApiEmployeeLedger = {
  employee?: { id?: number; code?: string | null; name?: string };
  filters?: {
    employee_id?: number | null;
    date_from?: string | null;
    date_to?: string | null;
  };
  lines?: Array<Record<string, unknown>>;
  totals?: {
    opening_balance?: number;
    opening_balance_side?: string;
    debit?: number;
    credit?: number;
    balance?: number;
    balance_side?: string;
  };
};

export type FetchEmployeeLedgerParams = {
  employeeId: string | number;
  dateFrom?: string | null;
  dateTo?: string | null;
};

function ledgerQueryParams(params: FetchEmployeeLedgerParams) {
  return {
    employee_id: Number(params.employeeId),
    ...(params.dateFrom ? { date_from: params.dateFrom } : {}),
    ...(params.dateTo ? { date_to: params.dateTo } : {}),
  };
}

async function downloadLedgerBlob(url: string, params: FetchEmployeeLedgerParams, filename: string): Promise<void> {
  const response = await api.get(url, {
    params: ledgerQueryParams(params),
    responseType: "blob",
  });
  const blob = new Blob([response.data], {
    type: response.headers["content-type"] || "application/octet-stream",
  });
  const href = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(href);
}

export async function downloadEmployeeLedgerPdf(params: FetchEmployeeLedgerParams): Promise<void> {
  const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
  await downloadLedgerBlob("/api/v1/employees/ledger/pdf", params, `employee-ledger-${stamp}.pdf`);
}

export async function downloadEmployeeLedgerExcel(params: FetchEmployeeLedgerParams): Promise<void> {
  const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
  await downloadLedgerBlob("/api/v1/employees/ledger/excel", params, `employee-ledger-${stamp}.csv`);
}

function asSide(value: unknown): "Dr" | "Cr" {
  return String(value ?? "").toLowerCase() === "cr" ? "Cr" : "Dr";
}

function mapLine(row: Record<string, unknown>): EmployeeLedgerLine {
  const particularsRaw = row.particulars;
  const particulars = Array.isArray(particularsRaw)
    ? particularsRaw.map((p) => String(p)).filter(Boolean)
    : typeof particularsRaw === "string" && particularsRaw.trim()
      ? [particularsRaw.trim()]
      : [];

  return {
    id: Number(row.id ?? 0),
    date: (row.date as string | null) ?? null,
    voucher: (row.voucher as string | null) ?? null,
    voucher_type: (row.voucher_type as string | null) ?? null,
    account_id: row.account_id != null ? Number(row.account_id) : null,
    account_code: (row.account_code as string | null) ?? null,
    account_name: (row.account_name as string | null) ?? null,
    particulars,
    from_to: (row.from_to as string | null) ?? null,
    debit: Number(row.debit ?? 0),
    credit: Number(row.credit ?? 0),
    balance: Number(row.balance ?? 0),
    balance_side: asSide(row.balance_side),
  };
}

export async function fetchEmployeeLedger(params: FetchEmployeeLedgerParams): Promise<EmployeeLedger> {
  const response = await api.get<{ data: ApiEmployeeLedger }>("/api/v1/employees/ledger", {
    params: ledgerQueryParams(params),
  });
  const data = response.data?.data ?? {};
  const employee = data.employee;
  return {
    employee: employee?.id
      ? {
          id: Number(employee.id),
          code: employee.code ?? null,
          name: employee.name ?? "",
        }
      : null,
    filters: {
      employee_id: data.filters?.employee_id != null ? Number(data.filters.employee_id) : null,
      date_from: data.filters?.date_from ?? null,
      date_to: data.filters?.date_to ?? null,
    },
    lines: Array.isArray(data.lines) ? data.lines.map(mapLine) : [],
    totals: {
      opening_balance: Number(data.totals?.opening_balance ?? 0),
      opening_balance_side: asSide(data.totals?.opening_balance_side),
      debit: Number(data.totals?.debit ?? 0),
      credit: Number(data.totals?.credit ?? 0),
      balance: Number(data.totals?.balance ?? 0),
      balance_side: asSide(data.totals?.balance_side),
    },
  };
}
